/**
 * Same-pool tabs share one Cognito cookie jar. This module keeps them aligned
 * without letting the other pool's tabs join in.
 *
 * - The pool is the one that authenticated this tab, not the URL. `/employee/*`
 *   is the employee pool; analytics, admin, and the viewers declare employee
 *   only when that init is the session they adopted.
 * - Activity is broadcast with that pool. A tab adopts activity for it alone, so
 *   idle is "now" minus the newest activity on that pool.
 * - A sign-in or cell switch that changes a pool's user or tenant tells the other
 *   tabs that have claimed that pool. A tab using that pool drops its in-memory
 *   session and stays on the login page. A tab using the other pool records the
 *   replacement and keeps its current session, so a later page does not adopt
 *   the new cookie. Tabs do not call `/api/sign-out`: `scope=local` clears both
 *   pools and would end the tab that just signed into the new tenant.
 * - The displaced tab records the fact in sessionStorage (per tab, not shared)
 *   so a reload does not quietly adopt the new cookie.
 */

import { poolForRoute, type AuthPool } from '@/lib/auth-pool';
import { idleClock } from '@/lib/session/idle-clock';

export const TAB_SYNC_CHANNEL = 'tai-session-activity';

/** Where a displaced tab waits until someone chooses to sign in on that tab. */
export const DISPLACED_LOGIN_HREF = '/login';

export type TabActivityMessage = {
  type: 'activity';
  pool: AuthPool;
  ts: number;
};

export type TabTenantMessage = {
  type: 'tenant';
  pool: AuthPool;
  /** Empty, with an empty tenantId, when a cell-switch clear has no next tenant yet. */
  userId: string;
  /** Empty when the next tenant is not known yet (clearing a cell switch). */
  tenantId: string;
  tabId: string;
};

export type TabSyncMessage = TabActivityMessage | TabTenantMessage;

const TAB_ID_KEY = 'tai-tab-id';
const CLAIM_SEP = '\n';
const tenantKey = (pool: AuthPool) => `tai-tab-tenant:${pool}`;
const displacedKey = (pool: AuthPool) => `tai-tab-displaced:${pool}`;

const memoryTenant: Partial<Record<AuthPool, string | null>> = {};
let memoryTabId: string | null = null;
/** Pool this document authenticated as. Null until init (or an /employee route) says. */
let activeAuthPool: AuthPool | null = null;
/** Surface that made `activeAuthPool`. A later surface does not inherit it. */
let claimSurface: string | null = null;
/** Pool whose stamp the client clock is currently following. */
let clockPool: AuthPool | null = null;
/** Previous route pool, so a customer route does not keep an employee claim from /employee. */
let routePoolForAuth: AuthPool | null = null;
/** Same-document copy of the displaced flag so a private-mode storage miss still sticks. */
const memoryDisplaced: Record<AuthPool, boolean> = { customer: false, employee: false };

/**
 * Pages that share one adopted session. `/analytics` and `/analytics/sessions`
 * are one surface. `/` is its own, so an employee claim from analytics does not
 * follow a client navigation onto the customer shell.
 */
const SESSION_SURFACES = [
  '/employee',
  '/analytics',
  '/admin',
  '/immigration-reports',
  '/meta-viewer',
  '/email-viewer',
  '/mobile-viewer',
  '/xlsx-viewer',
  '/debug-auth',
] as const;

export function sessionSurface(pathname: string): string {
  const path = pathname || '/';
  for (const prefix of SESSION_SURFACES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return prefix;
  }
  return path;
}

function currentPath(pathname?: string): string {
  if (typeof pathname === 'string') return pathname;
  if (typeof window !== 'undefined' && typeof window.location?.pathname === 'string') {
    return window.location.pathname;
  }
  return '/';
}

function releaseStaleAuthenticatedPool(pathname: string): void {
  const surface = sessionSurface(pathname);
  if (!claimSurface || claimSurface === surface) return;
  activeAuthPool = null;
  claimSurface = null;
}

/** `userId` + `tenantId`. Same tenant, different user, is a different session. */
export function sessionClaim(userId: string, tenantId: string): string {
  return `${userId}${CLAIM_SEP}${tenantId}`;
}

export function splitSessionClaim(claim: string): { userId: string; tenantId: string } {
  const splitAt = claim.indexOf(CLAIM_SEP);
  if (splitAt === -1) return { userId: '', tenantId: claim };
  return { userId: claim.slice(0, splitAt), tenantId: claim.slice(splitAt + 1) };
}

function browserStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readStorage(key: string): string | null {
  const storage = browserStorage();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  const storage = browserStorage();
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Private mode can reject the write. The in-memory copy still covers this document.
  }
}

function removeStorage(key: string): void {
  const storage = browserStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // See writeStorage.
  }
}

export function getTabId(): string {
  if (memoryTabId) return memoryTabId;
  const existing = readStorage(TAB_ID_KEY);
  if (existing) {
    memoryTabId = existing;
    return existing;
  }
  const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  memoryTabId = id;
  writeStorage(TAB_ID_KEY, id);
  return id;
}

export function readTabClaim(pool: AuthPool): string | null {
  if (typeof memoryTenant[pool] === 'string') return memoryTenant[pool] ?? null;
  return readStorage(tenantKey(pool));
}

export function authenticatedPool(): AuthPool | null {
  return activeAuthPool;
}

/**
 * Pool whose idle clock this tab should read and write.
 *
 * `/employee/*` is the employee pool immediately. Any other route stays unset
 * until its init adopts a session, so an analytics page does not stamp the
 * customer clock while it is still resolving an employee session.
 */
export function activityPool(routePool: AuthPool): AuthPool | null {
  if (activeAuthPool) return activeAuthPool;
  return routePool === 'employee' ? 'employee' : null;
}

/**
 * Forget an employee pool carried over from `/employee/*`, or from another
 * customer-route surface (analytics, admin, a viewer), when the next page has
 * not adopted it. A later init on that page sets the pool again if it really
 * did adopt the employee session.
 */
export function alignAuthenticatedPoolWithRoute(routePool: AuthPool, pathname?: string): void {
  const path = currentPath(pathname);
  releaseStaleAuthenticatedPool(path);
  const surface = sessionSurface(path);
  const prev = routePoolForAuth;
  routePoolForAuth = routePool;
  if (prev === null) {
    if (routePool === 'employee') {
      activeAuthPool = 'employee';
      claimSurface = surface;
    }
    return;
  }
  if (prev === routePool) return;
  if (routePool === 'employee') {
    activeAuthPool = 'employee';
    claimSurface = surface;
    return;
  }
  if (activeAuthPool === 'employee') {
    activeAuthPool = null;
    claimSurface = null;
  }
}

export function noteAuthenticatedPool(pool: AuthPool, pathname?: string): void {
  activeAuthPool = pool;
  claimSurface = sessionSurface(currentPath(pathname));
}

/**
 * Pool `refreshAuthSession` will apply for `pathname`.
 *
 * An employee claim stays only on the surface that made it. The customer shell
 * and every other surface refresh their own route pool, so a displaced customer
 * flag is not hidden behind that claim.
 */
export function prepareRefreshPool(pathname: string): AuthPool {
  releaseStaleAuthenticatedPool(pathname);
  if (pathname.startsWith('/employee/')) return 'employee';
  if (activeAuthPool === 'employee' && claimSurface === sessionSurface(pathname)) return 'employee';
  return 'customer';
}

/** A refresh may adopt its pool only while the tab is still on the surface that started it. */
export function shouldAdoptRefresh(startedSurface: string, pathname: string): boolean {
  return sessionSurface(pathname) === startedSurface;
}

export function isTabDisplaced(pool: AuthPool): boolean {
  return memoryDisplaced[pool] || readStorage(displacedKey(pool)) === '1';
}

/** Login redirect for `pool` alone. The other pool's flag does not count. */
export function displacedLoginHref(pool: AuthPool): string | null {
  return isTabDisplaced(pool) ? DISPLACED_LOGIN_HREF : null;
}

/**
 * Start navigation to the login page when the pool this tab is using was
 * displaced. Returns true when the caller must not adopt a session.
 */
export function redirectIfTabDisplaced(pool?: AuthPool): boolean {
  if (typeof window === 'undefined') return false;
  const active = pool ?? activityPool(poolForRoute(window.location.pathname));
  if (!active) return false;
  const href = displacedLoginHref(active);
  if (!href) return false;
  window.location.replace(href);
  return true;
}

export function markTabDisplaced(pool: AuthPool): void {
  memoryDisplaced[pool] = true;
  writeStorage(displacedKey(pool), '1');
}

/** The login button is an explicit choice to adopt whatever cookie is current. */
export function clearDisplacedTabs(): void {
  memoryDisplaced.customer = false;
  memoryDisplaced.employee = false;
  removeStorage(displacedKey('customer'));
  removeStorage(displacedKey('employee'));
}

export function activityApplies(messagePool: unknown, localPool: AuthPool): boolean {
  return messagePool === localPool;
}

/**
 * Another tab replaced this pool's user or tenant.
 *
 * Stay when the message is for the other pool, from this tab, or this tab has
 * not claimed that pool yet. An empty user and tenant is a cell-switch clear.
 */
export function shouldDisplaceTab(input: {
  messageTabId: string;
  localTabId: string;
  messagePool: AuthPool;
  localPool: AuthPool;
  rememberedClaim: string | null;
  messageUserId: string;
  messageTenantId: string;
}): boolean {
  if (input.messagePool !== input.localPool) return false;
  if (!input.messageTabId || input.messageTabId === input.localTabId) return false;
  if (!input.rememberedClaim) return false;
  if (input.messageUserId === '' && input.messageTenantId === '') return true;
  return input.rememberedClaim !== sessionClaim(input.messageUserId, input.messageTenantId);
}

/**
 * This tab signs out only when the replaced pool is the one it is using now.
 * The other pool's replacement stays recorded for the next page that adopts it.
 */
export function signsOutForDisplacement(
  activePool: AuthPool | null,
  messagePool: AuthPool,
): boolean {
  return activePool === messagePool;
}

/**
 * Replace the client idle stamp when this page's session is a different pool
 * from the one the clock is following. The first claim of the route's own pool
 * keeps the stamp SessionMount already seeded.
 */
export function shouldAdoptPoolStamp(
  followedPool: AuthPool | null,
  nextPool: AuthPool,
  routePool: AuthPool,
): boolean {
  if (followedPool === nextPool) return false;
  if (followedPool === null && nextPool === routePool) return false;
  return true;
}

function post(message: TabSyncMessage): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(TAB_SYNC_CHANNEL);
  channel.postMessage(message);
  channel.close();
}

function rememberClaim(pool: AuthPool, claim: string): void {
  memoryTenant[pool] = claim;
  writeStorage(tenantKey(pool), claim);
}

function userIdFromBody(body: { tokens?: unknown }): string {
  const tokens = body.tokens;
  if (!tokens || typeof tokens !== 'object') return '';
  const userId = (tokens as { userIdentity?: unknown }).userIdentity;
  return typeof userId === 'string' ? userId.trim() : '';
}

function stampFromBody(body: { lastActivityAt?: unknown }): number | null {
  return typeof body.lastActivityAt === 'number' && Number.isFinite(body.lastActivityAt)
    ? body.lastActivityAt
    : null;
}

function followPoolStamp(pool: AuthPool, stamp: number | null): void {
  const routePool = typeof window === 'undefined' ? pool : poolForRoute(window.location.pathname);
  if (stamp !== null && shouldAdoptPoolStamp(clockPool, pool, routePool)) {
    idleClock.adoptPoolStamp(stamp);
  }
  clockPool = pool;
}

/** Remember this tab's user and tenant, and tell other tabs when either changed. */
export function publishTabTenant(pool: AuthPool, tenantId: string, userId?: string): void {
  if (typeof window === 'undefined') return;
  const tenant = tenantId.trim();
  if (!tenant) return;
  noteAuthenticatedPool(pool);
  followPoolStamp(pool, null);
  const prev = readTabClaim(pool);
  const explicit = userId?.trim() ?? '';
  const user = explicit || (prev ? splitSessionClaim(prev).userId : '');
  const next = sessionClaim(user, tenant);
  rememberClaim(pool, next);
  if (prev === next) return;
  post({ type: 'tenant', pool, userId: user, tenantId: tenant, tabId: getTabId() });
}

export function publishTabTenantFromBody(
  pool: AuthPool,
  body: { tenantId?: unknown; lastActivityAt?: unknown; tokens?: unknown },
): boolean {
  if (typeof body.tenantId !== 'string') return false;
  if (isTabDisplaced(pool)) {
    if (typeof window !== 'undefined') window.location.replace(DISPLACED_LOGIN_HREF);
    return false;
  }
  if (typeof window !== 'undefined') {
    noteAuthenticatedPool(pool);
    followPoolStamp(pool, stampFromBody(body));
  }
  publishTabTenant(pool, body.tenantId, userIdFromBody(body));
  return true;
}

/** Cell-switch clear: the next tenant is not known until the following page load. */
export function publishPoolReplaced(pool: AuthPool): void {
  if (typeof window === 'undefined') return;
  noteAuthenticatedPool(pool);
  memoryTenant[pool] = null;
  removeStorage(tenantKey(pool));
  post({ type: 'tenant', pool, userId: '', tenantId: '', tabId: getTabId() });
}

export function isTabSyncMessage(data: unknown): data is TabSyncMessage {
  if (!data || typeof data !== 'object') return false;
  const message = data as { type?: unknown; pool?: unknown };
  if (message.pool !== 'customer' && message.pool !== 'employee') return false;
  if (message.type === 'activity') return typeof (data as { ts?: unknown }).ts === 'number';
  if (message.type === 'tenant') {
    const tenant = data as { tenantId?: unknown; userId?: unknown; tabId?: unknown };
    return typeof tenant.tenantId === 'string'
      && typeof tenant.userId === 'string'
      && typeof tenant.tabId === 'string';
  }
  return false;
}

/** Test seam. */
export function __resetTabSyncForTests(): void {
  activeAuthPool = null;
  claimSurface = null;
  clockPool = null;
  routePoolForAuth = null;
  memoryTabId = null;
  memoryTenant.customer = null;
  memoryTenant.employee = null;
  memoryDisplaced.customer = false;
  memoryDisplaced.employee = false;
}
